import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Text,
  SimpleGrid,
  Progress,
  Badge,
  Heading,
  useColorModeValue,
  Skeleton,
} from "@chakra-ui/react";
import {
  Users,
  UserCheck,
  ShieldCheck,
  ShieldOff,
  CircleCheck,
  CircleDashed,
  Image,
  FileText,
  Activity,
  Heart,
  MessageCircle,
  Sparkles,
} from "lucide-react";
import { computeAdminDashboardStats } from "../../util/adminStats";
import { GetAdminActivityStats } from "../../services/backend";

function MetricCard({ icon: Icon, label, value, sub, ...rest }) {
  const cardBg = useColorModeValue("rgba(255,255,255,0.75)", "rgba(255,255,255,0.04)");
  const border = useColorModeValue("gray.200", "whiteAlpha.100");
  const titleColor = useColorModeValue("gray.500", "whiteAlpha.500");
  const valueColor = useColorModeValue("gray.900", "white");

  return (
    <Box
      p={4}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={border}
      bg={cardBg}
      backdropFilter="blur(8px)"
      {...rest}
    >
      <Flex align="center" gap={3} mb={2}>
        <Flex
          w={9}
          h={9}
          borderRadius="full"
          align="center"
          justify="center"
          bgGradient="linear(to-br, brand.300, brand.500)"
          boxShadow="0 2px 10px rgba(52,211,153,0.25)"
        >
          <Icon size={18} color="#0a0a0b" strokeWidth={2.25} />
        </Flex>
        <Text fontSize="xs" fontWeight="semibold" color={titleColor} textTransform="uppercase" letterSpacing="0.06em">
          {label}
        </Text>
      </Flex>
      <Text fontSize="2xl" fontWeight="bold" color={valueColor} lineHeight="1.1" letterSpacing="-0.02em">
        {value}
      </Text>
      {sub ? (
        <Text fontSize="xs" color={titleColor} mt={1}>
          {sub}
        </Text>
      ) : null}
    </Box>
  );
}

function BreakdownBar({ label, value, total, colorScheme = "green" }) {
  const muted = useColorModeValue("gray.600", "whiteAlpha.600");
  const labelColor = useColorModeValue("gray.800", "whiteAlpha.900");
  const trackBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const percent = total ? Math.round((value / total) * 100) : 0;

  return (
    <Box mb={3} _last={{ mb: 0 }}>
      <Flex justify="space-between" align="center" mb={1.5} gap={2}>
        <Text fontSize="sm" color={labelColor} fontWeight="medium">
          {label}
        </Text>
        <Text fontSize="sm" color={muted} whiteSpace="nowrap">
          {value}{" "}
          <Text as="span" fontSize="xs">
            ({percent}%)
          </Text>
        </Text>
      </Flex>
      <Progress
        value={percent}
        size="sm"
        borderRadius="full"
        colorScheme={colorScheme}
        bg={trackBg}
      />
    </Box>
  );
}

function Panel({ title, children }) {
  const cardBg = useColorModeValue("rgba(255,255,255,0.92)", "rgba(18,18,20,0.88)");
  const border = useColorModeValue("gray.200", "whiteAlpha.120");
  const titleColor = useColorModeValue("gray.900", "white");

  return (
    <Box p={5} borderRadius="xl" borderWidth="1px" borderColor={border} bg={cardBg}>
      <Heading size="sm" color={titleColor} mb={4} letterSpacing="-0.02em">
        {title}
      </Heading>
      {children}
    </Box>
  );
}

const emptyActivity = {
  likesTotal: 0,
  likesToday: 0,
  messagesTotal: 0,
  messagesToday: 0,
  matchesTotal: 0,
  matchesToday: 0,
};

export default function AdminDashboard({ users, loading }) {
  const stats = useMemo(() => computeAdminDashboardStats(users), [users]);
  const [activity, setActivity] = useState(emptyActivity);
  const [activityLoading, setActivityLoading] = useState(true);

  // Reload with Overview when Refresh reloads users.
  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    (async () => {
      setActivityLoading(true);
      try {
        const data = await GetAdminActivityStats();
        if (!cancelled) setActivity({ ...emptyActivity, ...data });
      } catch (err) {
        console.error("Failed to load admin activity stats:", err);
        if (!cancelled) setActivity(emptyActivity);
      } finally {
        if (!cancelled) setActivityLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, users]);

  const heroBg = useColorModeValue(
    "linear-gradient(135deg, rgba(52,211,153,0.12) 0%, rgba(234,179,8,0.06) 50%, rgba(18,18,20,0) 100%)",
    "linear-gradient(135deg, rgba(52,211,153,0.18) 0%, rgba(234,179,8,0.08) 40%, rgba(10,10,11,0.4) 100%)"
  );
  const border = useColorModeValue("gray.200", "whiteAlpha.150");
  const muted = useColorModeValue("gray.600", "whiteAlpha.600");
  const titleColor = useColorModeValue("gray.900", "white");

  if (loading) {
    return (
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} height="120px" borderRadius="xl" />
        ))}
      </SimpleGrid>
    );
  }

  const healthBadgeScheme =
    stats.healthTone === "green" ? "green" : stats.healthTone === "yellow" ? "yellow" : "orange";

  const formatCount = (n) => (Number.isFinite(n) ? n.toLocaleString() : "0");

  return (
    <Box>
      <Box
        p={{ base: 5, md: 6 }}
        mb={5}
        borderRadius="2xl"
        borderWidth="1px"
        borderColor={border}
        bg={heroBg}
        position="relative"
        overflow="hidden"
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          align={{ base: "flex-start", md: "center" }}
          justify="space-between"
          gap={4}
        >
          <Flex align="center" gap={4}>
            <Flex
              w={12}
              h={12}
              borderRadius="xl"
              bg="brand.500"
              align="center"
              justify="center"
              boxShadow="0 0 24px rgba(52,211,153,0.35)"
            >
              <Activity size={24} color="#0a0a0b" strokeWidth={2.5} />
            </Flex>
            <Box>
              <Text fontSize="xs" color={muted} textTransform="uppercase" letterSpacing="0.1em" mb={0.5}>
                Application health
              </Text>
              <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" color={titleColor} letterSpacing="-0.03em">
                {stats.healthScore}%
              </Text>
              <Text fontSize="sm" color={muted}>
                Based on profile completion, photos, bios, and verification
              </Text>
            </Box>
          </Flex>
          <Badge
            colorScheme={healthBadgeScheme}
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
            alignSelf={{ base: "flex-start", md: "center" }}
          >
            {stats.healthLabel}
          </Badge>
        </Flex>
      </Box>

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={5}>
        <MetricCard icon={Users} label="Total users" value={stats.total} />
        <MetricCard
          icon={UserCheck}
          label="Avg age"
          value={stats.avgAge ?? "—"}
          sub={stats.avgAge ? "Among users with age set" : "No age data"}
        />
        <MetricCard icon={CircleCheck} label="Profiles complete" value={stats.complete} sub={`${stats.pctComplete}% of users`} />
        <MetricCard icon={ShieldCheck} label="Verified" value={stats.verified} sub={`${stats.pctVerified}% of users`} />
      </SimpleGrid>

      <Heading size="sm" color={titleColor} mb={3} letterSpacing="-0.02em">
        App activity
      </Heading>
      {activityLoading ? (
        <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4} mb={5}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height="110px" borderRadius="xl" />
          ))}
        </SimpleGrid>
      ) : (
        <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4} mb={5}>
          <MetricCard
            icon={Heart}
            label="Likes sent"
            value={formatCount(activity.likesTotal)}
            sub={`${formatCount(activity.likesToday)} today`}
          />
          <MetricCard
            icon={MessageCircle}
            label="Messages sent"
            value={formatCount(activity.messagesTotal)}
            sub={`${formatCount(activity.messagesToday)} today`}
          />
          <MetricCard
            icon={Sparkles}
            label="Matches"
            value={formatCount(activity.matchesTotal)}
            sub={`${formatCount(activity.matchesToday)} today`}
          />
        </SimpleGrid>
      )}

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} mb={5}>
        <Panel title="Gender breakdown">
          <BreakdownBar label="Female" value={stats.female} total={stats.total} colorScheme="pink" />
          <BreakdownBar label="Male" value={stats.male} total={stats.total} colorScheme="blue" />
          <BreakdownBar label="Other / not set" value={stats.otherGender} total={stats.total} colorScheme="gray" />
        </Panel>

        <Panel title="Profile status">
          <BreakdownBar label="Completed" value={stats.complete} total={stats.total} colorScheme="green" />
          <BreakdownBar label="Incomplete" value={stats.incomplete} total={stats.total} colorScheme="orange" />
          <BreakdownBar label="Verified" value={stats.verified} total={stats.total} colorScheme="green" />
          <BreakdownBar label="Unverified" value={stats.unverified} total={stats.total} colorScheme="yellow" />
        </Panel>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <Panel title="Profile quality">
          <BreakdownBar label="Has photos" value={stats.withPhotos} total={stats.total} colorScheme="cyan" />
          <BreakdownBar label="Has bio" value={stats.withBio} total={stats.total} colorScheme="purple" />
          <Flex mt={4} gap={3} flexWrap="wrap">
            <Flex align="center" gap={2} fontSize="sm" color={muted}>
              <Image size={14} />
              <Text>{stats.pctWithPhotos}% with photos</Text>
            </Flex>
            <Flex align="center" gap={2} fontSize="sm" color={muted}>
              <FileText size={14} />
              <Text>{stats.pctWithBio}% with bio</Text>
            </Flex>
          </Flex>
        </Panel>

        <Panel title="Top countries">
          {stats.topCountries.length === 0 ? (
            <Text fontSize="sm" color={muted}>
              No country data yet.
            </Text>
          ) : (
            stats.topCountries.map(({ name, count }) => (
              <BreakdownBar
                key={name}
                label={name}
                value={count}
                total={stats.total}
                colorScheme="green"
              />
            ))
          )}
        </Panel>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mt={5}>
        <Flex align="center" gap={2} p={3} borderRadius="lg" borderWidth="1px" borderColor={border} fontSize="sm" color={muted}>
          <CircleCheck size={16} color="var(--chakra-colors-green-400)" />
          <Text>
            <Text as="span" fontWeight="semibold" color={titleColor}>
              {stats.complete}
            </Text>{" "}
            completed
          </Text>
        </Flex>
        <Flex align="center" gap={2} p={3} borderRadius="lg" borderWidth="1px" borderColor={border} fontSize="sm" color={muted}>
          <CircleDashed size={16} />
          <Text>
            <Text as="span" fontWeight="semibold" color={titleColor}>
              {stats.incomplete}
            </Text>{" "}
            incomplete
          </Text>
        </Flex>
        <Flex align="center" gap={2} p={3} borderRadius="lg" borderWidth="1px" borderColor={border} fontSize="sm" color={muted}>
          <ShieldCheck size={16} />
          <Text>
            <Text as="span" fontWeight="semibold" color={titleColor}>
              {stats.verified}
            </Text>{" "}
            verified
          </Text>
        </Flex>
        <Flex align="center" gap={2} p={3} borderRadius="lg" borderWidth="1px" borderColor={border} fontSize="sm" color={muted}>
          <ShieldOff size={16} />
          <Text>
            <Text as="span" fontWeight="semibold" color={titleColor}>
              {stats.unverified}
            </Text>{" "}
            unverified
          </Text>
        </Flex>
      </SimpleGrid>
    </Box>
  );
}
